import flourite from "flourite";
import { toggleMark } from "prosemirror-commands";
import { Fragment, Node, Slice } from "prosemirror-model";
import { NodeSelection, TextSelection } from "prosemirror-state";
import { type EditorView } from "prosemirror-view";
import { useCallback, useEffect, useRef } from "react";

import { parsePage } from "@moment/api-collab";
import { isValidHttpUrl } from "@moment/design-system";
import { schema } from "@moment/prosemirror-schema";
import {
	useDocumentFromCurrentRoute,
	useFlushCompilation,
	usePageFromCurrentRoute,
} from "@moment/state";

import { markActive } from "../easel/prosemirror/helpers/markActive";
import { useComputeCells } from "./useComputeCells";

const NON_CODE_LANGUAGES = new Set(["unknown", "markdown", "yaml", "sql", "lua"]);

/**
 * Detect whether text is source code using flourite language detection.
 * Returns the detected language name (shiki-compatible) or null if
 * the text should be treated as markdown/prose.
 *
 * flourite can misidentify markdown as yaml/lua/sql due to punctuation,
 * so those ambiguous languages are excluded. If flourite returns one of
 * them, we fall through to the markdown parser which will handle it.
 */
export function detectCodeLanguage(text: string): string | null {
	const result = flourite(text, { shiki: true });
	if (NON_CODE_LANGUAGES.has(result.language)) return null;
	return result.language;
}

export function pasteAsRawText(view: EditorView, text: string): void {
	const lines = text.split(/\r?\n/);
	const nodes = lines.map((line) =>
		schema.nodes.paragraph.create(null, line ? schema.text(line) : undefined)
	);
	const slice = new Slice(Fragment.from(nodes), 1, 1);
	view.dispatch(
		view.state.tr
			.replaceSelection(slice)
			.scrollIntoView()
			.setMeta("paste", true)
			.setMeta("uiEvent", "paste")
	);
}

export function pasteAsCodeBlock(view: EditorView, text: string, language?: string | null): void {
	const codeBlock = schema.nodes.code_block.create(
		{ language: language ?? null },
		text ? schema.text(text) : undefined
	);
	const codeSlice = new Slice(Fragment.from(codeBlock), 0, 0);
	view.dispatch(
		view.state.tr
			.replaceSelection(codeSlice)
			.scrollIntoView()
			.setMeta("paste", true)
			.setMeta("uiEvent", "paste")
	);
}

/**
 * Paste plain text as rich text by parsing it as markdown.
 * Used by both the keyboard paste handler and the context menu paste action.
 */
export function pasteTextAsMarkdown(view: EditorView, text: string): void {
	try {
		const { doc, metadata } = parsePage(text);
		const pmDoc = Node.fromJSON(schema, doc);

		const nodes: Node[] = [];

		if (metadata && Object.keys(metadata).length > 0) {
			const yamlText = text.match(/^---\n([\s\S]*?)\n---/)?.[0];
			if (yamlText) {
				nodes.push(
					schema.nodes.code_block.create({ language: "yaml" }, schema.text(yamlText))
				);
			}
		}
		pmDoc.content.forEach((node) => nodes.push(node));

		const combined = Fragment.from(nodes);
		const mdSlice = Slice.maxOpen(combined, false);
		view.dispatch(
			view.state.tr
				.replaceSelection(mdSlice)
				.scrollIntoView()
				.setMeta("paste", true)
				.setMeta("uiEvent", "paste")
		);
	} catch {
		view.pasteText(text);
	}
}

export const useEaselProseMirrorPasteHandler = () => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();
	const { makeUniqueCellName } = useComputeCells(documentId, pageId);
	const flushCompilation = useFlushCompilation(documentId ?? "", pageId ?? "");

	const shiftHeldRef = useRef(false);
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.shiftKey) shiftHeldRef.current = true;
		};
		const onKeyUp = (e: KeyboardEvent) => {
			if (!e.shiftKey) shiftHeldRef.current = false;
		};
		const onBlur = () => {
			shiftHeldRef.current = false;
		};
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		window.addEventListener("blur", onBlur);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
			window.removeEventListener("blur", onBlur);
		};
	}, []);

	return useCallback(
		(view: EditorView, e: ClipboardEvent, slice: Slice) => {
			const firstNode = slice.content.firstChild;
			const pastingSingleComputeCell =
				slice.content.childCount === 1 && firstNode?.type.name === "compute_cell";

			// Cmd+Shift+V / Ctrl+Shift+V → paste as raw text (no formatting)
			if (shiftHeldRef.current) {
				const isInsideCodeBlock = view.state.selection.$from.parent.type.spec.code === true;
				if (isInsideCodeBlock) {
					return false; // Let ProseMirror handle plain text paste natively inside code blocks
				}
				const plainText = e.clipboardData?.getData("text/plain");
				if (plainText) {
					pasteAsRawText(view, plainText);
					return true;
				}
				return false;
			}

			// If we're pasting a link, try to simply add a link mark to the selection.
			const href = e.clipboardData?.getData("text/plain").trim();
			if (!pastingSingleComputeCell && isValidHttpUrl(href)) {
				const { state, dispatch } = view;
				const { selection, tr, schema } = state;
				const linkMark = schema.marks["link"];
				const { $from, $to } = selection;

				// Paste the text as normal if the selection is 0-width.
				if (!linkMark || $from.pos === $to.pos) {
					return false;
				}

				if (!markActive(state, linkMark)) {
					// if there's no mark in the selection, add it
					toggleMark(linkMark, { href })(state, dispatch);

					// Set selection to 0 width at end of paste so that the editor toolbar goes
					// away. This matches Coda and Notion's behavior in this case.
					const { tr } = view.state;
					tr.setSelection(TextSelection.create(tr.doc, $to.pos));

					// Dispatch.
					dispatch(tr.scrollIntoView());
				} else {
					// If the mark is active, remove the entire link mark to avoid having weird
					// links that are split into pieces on a per-character basis.
					const markPositions = [];
					state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
						if (node.marks.some((mark) => mark.type === linkMark)) {
							markPositions.push({ from: pos, to: pos + node.nodeSize });
						}
					});

					markPositions.forEach(({ from, to }) => {
						tr.removeMark(from, to, linkMark);
					});

					tr.addMark($from.pos, $to.pos, linkMark.create({ href }));

					// Set selection to 0 width at end of paste so that the editor toolbar goes
					// away. This matches Coda and Notion's behavior in this case.
					tr.setSelection(TextSelection.create(tr.doc, $to.pos));

					// Dispatch.
					dispatch(tr.scrollIntoView());
				}

				return true;
			}

			// Proceed normally if we're not pasting a single `compute_cell`.
			if (!pastingSingleComputeCell) {
				const plainText = e.clipboardData?.getData("text/plain");
				const isInsideCodeBlock = view.state.selection.$from.parent.type.spec.code === true;

				if (plainText && !isInsideCodeBlock) {
					const html = e.clipboardData?.getData("text/html");

					if (html?.includes("data-pm-slice")) {
						return false;
					}

					const parsedAsSingleCodeBlock =
						slice.content.childCount === 1 &&
						slice.content.firstChild?.type.name === "code_block";
					if (html && !parsedAsSingleCodeBlock) {
						return false;
					}

					pasteTextAsMarkdown(view, plainText);
					return true;
				}

				return false;
			}

			const attrs = firstNode.attrs;
			let tr = view.state.tr;

			// IMPORTANT: Backwards compatibility for when `code` was stored in Node attrs instead of
			// text content.
			const code = attrs["code"] || firstNode.textContent;
			// Don't collide with the cell we're copying from.
			const cellName = makeUniqueCellName(attrs["cellName"]);
			const cellToInsert = schema.nodes.compute_cell.create(
				{ ...attrs, cellName },
				Fragment.from(schema.text(code))
			);

			const sel = view.state.selection;
			let insertPos = sel.$head.end(sel.$head.depth);
			if (sel instanceof NodeSelection) {
				// `NodeSelection#end()` will returns the position of the parent node (often doc,
				// which would be 0).
				insertPos = sel.$head.pos;
			}

			const fromNode = sel.$from.node(sel.$from.depth);
			const toNode = sel.$to.node(sel.$to.depth);
			if (
				fromNode === toNode &&
				fromNode.type === schema.nodes.paragraph &&
				fromNode.content.size === 0
			) {
				tr = tr.delete(sel.from - 1, sel.to + 1);
			}
			tr = tr.insert(tr.mapping.map(insertPos), Fragment.from(cellToInsert));

			view.dispatch(tr);
			view.focus();

			// Flush compilation immediately so the pasted cell runs without the debounce delay.
			flushCompilation();

			return true;
		},
		[makeUniqueCellName, flushCompilation]
	);
};
